// 051 — Página 2: Partograma + Recién Nacido/Egresos (Fase 4, Tanda 2, ver
// PLAN.md). `Formulario051.tsx` lo inserta completo, después de
// `Bloques051Parto.tsx` (Tanda 1b) — cierra 051. El gráfico bespoke
// (`PartogramaVisual`) y la tabla horaria a-p viven en `partograma/`; este
// archivo solo agrega los bloques de checklist/texto (near miss, recién
// nacido, egresos) alrededor de ellos.
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CampoDictado } from './CampoDictado';
import { ChecklistGrid } from './ChecklistGrid';
import { SelectGrid } from './SelectGrid';
import { GrillaSiNo } from './Grillas051';
import { SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import {
  ALIMENTO_AL_ALTA_OPCIONES,
  ATENDIO_RN_OPCIONES,
  CONDICION_EGRESO_MATERNO_OPCIONES,
  CONDICION_EGRESO_RN_OPCIONES,
  DEFECTOS_CONGENITOS_OPCIONES,
  EG_FUENTE_RN_OPCIONES,
  ENFERMEDADES_RN_OPCIONES,
  METODO_ACCEDIDO_OPCIONES,
  METODO_ANTICONCEPCION_OPCIONES,
  NEAR_MISS_CRITERIOS_CLINICOS_OPCIONES,
  NEAR_MISS_INTERVENCIONES_SIMPLES_OPCIONES,
  NEAR_MISS_LABORATORIO_ITEMS_OPCIONES,
  NEAR_MISS_LABORATORIO_OPCIONES,
  NEAR_MISS_SI_NO_OPCIONES,
  PESO_EG_RN_OPCIONES,
  PESO_NACER_ALERTA_OPCIONES,
  REANIMACION_RN_OPCIONES,
  SEXO_RN_OPCIONES,
  TAMIZAJE_AUDITIVO_OPCIONES,
  TAMIZAJE_METABOLICO_OPCIONES,
  TRASLADO_RN_OPCIONES,
  estadoInicialEgresoMaterno051,
  estadoInicialEgresoRn051,
  estadoInicialNearMiss051,
  estadoInicialRecienNacido051,
  type AlimentoAlAlta,
  type CondicionEgresoMaterno,
  type CondicionEgresoRn,
  type DefectosCongenitos,
  type EgFuenteRn,
  type EnfermedadesRn,
  type EstadoEgresoMaterno051,
  type EstadoEgresoRn051,
  type EstadoNearMiss051,
  type EstadoRecienNacido051,
  type PesoEgRn,
  type PesoNacerAlerta,
  type SexoRn,
  type TamizajeMetabolico,
  type TrasladoRn,
} from './bloques051PartogramaDatos';
import { estadoInicialPartogramaVisual051, PartogramaVisual, type EstadoPartogramaVisual051 } from './partograma/PartogramaVisual';
import { estadoInicialTablaHorariaPartograma051, TablaHoraria, type EstadoTablaHorariaPartograma051 } from './partograma/TablaHoraria';

function CampoTexto({ id, label, value, disabled, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly disabled?: boolean; readonly onChange: (v: string) => void }): JSX.Element {
  return <CampoDictado id={id} etiqueta={label} valor={value} soloLectura={disabled} onChange={onChange} />;
}

function CampoFecha({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Input id={id} type="date" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function CampoSelect<T extends string>({
  id,
  label,
  value,
  opciones,
  etiquetaOpcion,
  onChange,
}: {
  readonly id: string;
  readonly label: string;
  readonly value: T | '';
  readonly opciones: readonly T[];
  readonly etiquetaOpcion: (op: T) => string;
  readonly onChange: (v: T) => void;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value || undefined} onValueChange={(v) => onChange(v as T)}>
        <SelectTrigger id={id}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {opciones.map((op) => (
            <SelectItem key={op} value={op}>
              {etiquetaOpcion(op)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function CampoCheckbox({ id, label, checked, onChange }: { readonly id: string; readonly label: string; readonly checked: boolean; readonly onChange: (v: boolean) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
    </div>
  );
}

function BloqueNearMiss({ estado, onChange }: { readonly estado: EstadoNearMiss051; readonly onChange: (v: EstadoNearMiss051) => void }): JSX.Element {
  const { t } = useTranslation();
  function actualizar(cambios: Partial<EstadoNearMiss051>): void {
    onChange({ ...estado, ...cambios });
  }
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f051.nearMiss.titulo')}</h4>
      <CampoSelect
        id="f051-nm-presente"
        label={t('formularios.f051.nearMiss.presente')}
        value={estado.presente}
        opciones={NEAR_MISS_SI_NO_OPCIONES}
        etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
        onChange={(v) => actualizar({ presente: v })}
      />

      {estado.presente === 'no' ? null : (
        <>
        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f051.nearMiss.criteriosClinicosTitulo')}</Label>
          <SelectGrid
            claves={NEAR_MISS_CRITERIOS_CLINICOS_OPCIONES}
            opciones={NEAR_MISS_SI_NO_OPCIONES}
            prefijoI18n="formularios.f051.nearMiss.criteriosClinicos"
            prefijoI18nOpciones="formularios.f051.nearMiss.opcionesSiNo"
            prefijoId="f051-nm-clinico"
            valores={estado.criteriosClinicos}
            onChange={(v) => actualizar({ criteriosClinicos: v })}
          />
        </div>

        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f051.nearMiss.laboratorioTitulo')}</Label>
          <SelectGrid
            claves={NEAR_MISS_LABORATORIO_ITEMS_OPCIONES}
            opciones={NEAR_MISS_LABORATORIO_OPCIONES}
            prefijoI18n="formularios.f051.nearMiss.laboratorio"
            prefijoI18nOpciones="formularios.f051.nearMiss.opcionesLaboratorio"
            prefijoId="f051-nm-lab"
            valores={estado.laboratorio}
            onChange={(v) => actualizar({ laboratorio: v })}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CampoSelect
            id="f051-nm-vasoactivos"
            label={t('formularios.f051.nearMiss.agentesVasoactivos')}
            value={estado.agentesVasoactivos}
            opciones={NEAR_MISS_SI_NO_OPCIONES}
            etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
            onChange={(v) => actualizar({ agentesVasoactivos: v })}
          />
          {estado.agentesVasoactivos === 'si' ? <CampoTexto id="f051-nm-vasoactivos-cual" label={t('formularios.f051.nearMiss.agentesVasoactivosCual')} value={estado.agentesVasoactivosCual} onChange={(v) => actualizar({ agentesVasoactivosCual: v })} /> : null}
          <CampoSelect
            id="f051-nm-intubacion"
            label={t('formularios.f051.nearMiss.intubacionVentilacion')}
            value={estado.intubacionVentilacion}
            opciones={NEAR_MISS_SI_NO_OPCIONES}
            etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
            onChange={(v) => actualizar({ intubacionVentilacion: v })}
          />
          {estado.intubacionVentilacion === 'si' ? <CampoTexto id="f051-nm-intubacion-dias" label={t('formularios.f051.nearMiss.intubacionVentilacionDias')} value={estado.intubacionVentilacionDias} onChange={(v) => actualizar({ intubacionVentilacionDias: v })} /> : null}
        </div>

        <div className="flex flex-col gap-1">
          <Label>{t('formularios.f051.nearMiss.intervencionesSimplesTitulo')}</Label>
          <SelectGrid
            claves={NEAR_MISS_INTERVENCIONES_SIMPLES_OPCIONES}
            opciones={NEAR_MISS_SI_NO_OPCIONES}
            prefijoI18n="formularios.f051.nearMiss.intervencionesSimples"
            prefijoI18nOpciones="formularios.f051.nearMiss.opcionesSiNo"
            prefijoId="f051-nm-iv"
            valores={estado.intervencionesSimples}
            onChange={(v) => actualizar({ intervencionesSimples: v })}
          />
        </div>
        {estado.intervencionesSimples.ingresoUci7Dias === 'si' ? <CampoTexto id="f051-nm-uci-dias" label={t('formularios.f051.nearMiss.ingresoUciDias')} value={estado.ingresoUciDias} onChange={(v) => actualizar({ ingresoUciDias: v })} /> : null}
        </>
      )}
    </div>
  );
}

function BloqueRecienNacido({ estado, onChange }: { readonly estado: EstadoRecienNacido051; readonly onChange: (v: EstadoRecienNacido051) => void }): JSX.Element {
  const { t } = useTranslation();
  function actualizar(cambios: Partial<EstadoRecienNacido051>): void {
    onChange({ ...estado, ...cambios });
  }
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f051.recienNacido.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoSelect<SexoRn>
          id="f051-rn-sexo"
          label={t('formularios.f051.recienNacido.sexo')}
          value={estado.sexo}
          opciones={SEXO_RN_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.recienNacido.sexoOpciones.${op}`)}
          onChange={(v) => actualizar({ sexo: v })}
        />
        <CampoTexto id="f051-rn-peso" label={t('formularios.f051.recienNacido.pesoAlNacer')} value={estado.pesoAlNacer} onChange={(v) => actualizar({ pesoAlNacer: v })} />
        <CampoSelect<PesoNacerAlerta>
          id="f051-rn-peso-alerta"
          label={t('formularios.f051.recienNacido.pesoAlNacerAlerta')}
          value={estado.pesoAlNacerAlerta}
          opciones={PESO_NACER_ALERTA_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.recienNacido.pesoAlNacerAlertaOpciones.${op}`)}
          onChange={(v) => actualizar({ pesoAlNacerAlerta: v })}
        />
        <CampoTexto id="f051-rn-longitud" label={t('formularios.f051.recienNacido.longitud')} value={estado.longitud} onChange={(v) => actualizar({ longitud: v })} />
        <CampoTexto id="f051-rn-pc" label={t('formularios.f051.recienNacido.perimetroCefalico')} value={estado.perimetroCefalico} onChange={(v) => actualizar({ perimetroCefalico: v })} />
        <CampoTexto id="f051-rn-eg-sem" label={t('formularios.f051.recienNacido.edadGestacionalSemanas')} value={estado.edadGestacionalSemanas} onChange={(v) => actualizar({ edadGestacionalSemanas: v })} />
        <CampoTexto id="f051-rn-eg-dias" label={t('formularios.f051.recienNacido.edadGestacionalDias')} value={estado.edadGestacionalDias} onChange={(v) => actualizar({ edadGestacionalDias: v })} />
        <CampoSelect<EgFuenteRn>
          id="f051-rn-eg-fuente"
          label={t('formularios.f051.recienNacido.egFuente')}
          value={estado.egFuente}
          opciones={EG_FUENTE_RN_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.recienNacido.egFuenteOpciones.${op}`)}
          onChange={(v) => actualizar({ egFuente: v })}
        />
        <CampoSelect<PesoEgRn>
          id="f051-rn-peso-eg"
          label={t('formularios.f051.recienNacido.pesoEg')}
          value={estado.pesoEg}
          opciones={PESO_EG_RN_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.recienNacido.pesoEgOpciones.${op}`)}
          onChange={(v) => actualizar({ pesoEg: v })}
        />
        <CampoTexto id="f051-rn-apgar1" label={t('formularios.f051.recienNacido.apgar1')} value={estado.apgar1} onChange={(v) => actualizar({ apgar1: v })} />
        <CampoTexto id="f051-rn-apgar5" label={t('formularios.f051.recienNacido.apgar5')} value={estado.apgar5} onChange={(v) => actualizar({ apgar5: v })} />
        <CampoTexto id="f051-rn-apgar10" label={t('formularios.f051.recienNacido.apgar10')} value={estado.apgarNoAplica ? '' : estado.apgar10} disabled={estado.apgarNoAplica} onChange={(v) => actualizar({ apgar10: v })} />
        <CampoTexto id="f051-rn-atendio-nombre" label={t('formularios.f051.recienNacido.atendioNombre')} value={estado.atendioNombre} onChange={(v) => actualizar({ atendioNombre: v })} />
        <CampoSelect<TrasladoRn>
          id="f051-rn-traslado"
          label={t('formularios.f051.recienNacido.traslado')}
          value={estado.traslado}
          opciones={TRASLADO_RN_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.recienNacido.trasladoOpciones.${op}`)}
          onChange={(v) => actualizar({ traslado: v })}
        />
        <CampoTexto id="f051-rn-tipificacion" label={t('formularios.f051.recienNacido.tipificacionRn')} value={estado.tipificacionRn} onChange={(v) => actualizar({ tipificacionRn: v })} />
        {estado.defectosCongenitos === 'no' ? null : <CampoTexto id="f051-rn-defectos-cie10" label={t('formularios.f051.recienNacido.defectosCongenitosCie10')} value={estado.defectosCongenitosCie10} onChange={(v) => actualizar({ defectosCongenitosCie10: v })} />}
        {estado.enfermedades === 'ninguna' ? null : (
          <>
            <SelectorDiagnosticoCie10 label={t('formularios.f051.recienNacido.enfermedadesCie10Fila1')} value={estado.enfermedadesDx[0]} onChange={(v) => actualizar({ enfermedadesDx: [v, estado.enfermedadesDx[1]] })} />
            <SelectorDiagnosticoCie10 label={t('formularios.f051.recienNacido.enfermedadesCie10Fila2')} value={estado.enfermedadesDx[1]} onChange={(v) => actualizar({ enfermedadesDx: [estado.enfermedadesDx[0], v] })} />
          </>
        )}
      </div>

      <CampoCheckbox id="f051-rn-apgar-na" label={t('formularios.f051.recienNacido.apgarNoAplica')} checked={estado.apgarNoAplica} onChange={(v) => actualizar({ apgarNoAplica: v })} />

      <GrillaSiNo titulo={t('formularios.f051.recienNacido.reanimacion.titulo')} claves={REANIMACION_RN_OPCIONES} prefijoI18n="formularios.f051.recienNacido.reanimacion.opciones" prefijoId="f051-rn-reanim" valores={estado.reanimacion} onChange={(v) => actualizar({ reanimacion: v })} />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <CampoSelect
          id="f051-rn-fallece-lugar"
          label={t('formularios.f051.recienNacido.falleceEnLugarParto')}
          value={estado.falleceEnLugarParto}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ falleceEnLugarParto: v })}
        />
        <CampoSelect
          id="f051-rn-vitamina-k"
          label={t('formularios.f051.recienNacido.vitaminaK')}
          value={estado.vitaminaK}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ vitaminaK: v })}
        />
        <CampoSelect
          id="f051-rn-profilaxis"
          label={t('formularios.f051.recienNacido.profilaxisOcular')}
          value={estado.profilaxisOcular}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ profilaxisOcular: v })}
        />
        <CampoSelect
          id="f051-rn-canguro-amerita"
          label={t('formularios.f051.recienNacido.metodoCanguroAmerita')}
          value={estado.metodoCanguroAmerita}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ metodoCanguroAmerita: v })}
        />
        {estado.metodoCanguroAmerita === 'no' ? null : (
          <CampoSelect
            id="f051-rn-canguro-aplica"
            label={t('formularios.f051.recienNacido.metodoCanguroSeAplica')}
            value={estado.metodoCanguroSeAplica}
            opciones={NEAR_MISS_SI_NO_OPCIONES}
            etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
            onChange={(v) => actualizar({ metodoCanguroSeAplica: v })}
          />
        )}
        <CampoSelect
          id="f051-rn-examen-normal"
          label={t('formularios.f051.recienNacido.examenFisicoNormal')}
          value={estado.examenFisicoNormal}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ examenFisicoNormal: v })}
        />
        <CampoSelect<DefectosCongenitos>
          id="f051-rn-defectos"
          label={t('formularios.f051.recienNacido.defectosCongenitos')}
          value={estado.defectosCongenitos}
          opciones={DEFECTOS_CONGENITOS_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.recienNacido.defectosCongenitosOpciones.${op}`)}
          onChange={(v) => actualizar({ defectosCongenitos: v })}
        />
        <CampoSelect<EnfermedadesRn>
          id="f051-rn-enfermedades"
          label={t('formularios.f051.recienNacido.enfermedades')}
          value={estado.enfermedades}
          opciones={ENFERMEDADES_RN_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.recienNacido.enfermedadesOpciones.${op}`)}
          onChange={(v) => actualizar({ enfermedades: v })}
        />
        <CampoSelect<TamizajeMetabolico>
          id="f051-rn-tamizaje-metabolico"
          label={t('formularios.f051.recienNacido.tamizajeMetabolico')}
          value={estado.tamizajeMetabolico}
          opciones={TAMIZAJE_METABOLICO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.recienNacido.tamizajeMetabolicoOpciones.${op}`)}
          onChange={(v) => actualizar({ tamizajeMetabolico: v })}
        />
        <CampoSelect
          id="f051-rn-hep-b"
          label={t('formularios.f051.recienNacido.vacunaHepatitisBDosis0')}
          value={estado.vacunaHepatitisBDosis0}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ vacunaHepatitisBDosis0: v })}
        />
        <CampoSelect
          id="f051-rn-apego"
          label={t('formularios.f051.recienNacido.apegoInmediato')}
          value={estado.apegoInmediato}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ apegoInmediato: v })}
        />
        <CampoSelect
          id="f051-rn-meconio"
          label={t('formularios.f051.recienNacido.meconio1erDia')}
          value={estado.meconio1erDia}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ meconio1erDia: v })}
        />
        <CampoSelect
          id="f051-rn-lactancia"
          label={t('formularios.f051.recienNacido.lactanciaInmediata')}
          value={estado.lactanciaInmediata}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ lactanciaInmediata: v })}
        />
      </div>

      <div className="flex flex-col gap-1">
        <Label>{t('formularios.f051.recienNacido.atendioParto.titulo')}</Label>
        <ChecklistGrid claves={ATENDIO_RN_OPCIONES} prefijoI18n="formularios.f051.recienNacido.atendio.opciones" prefijoId="f051-rn-atendio-parto" valores={estado.atendioParto} onChange={(v) => actualizar({ atendioParto: v })} />
      </div>
      <div className="flex flex-col gap-1">
        <Label>{t('formularios.f051.recienNacido.atendioNeonato.titulo')}</Label>
        <ChecklistGrid claves={ATENDIO_RN_OPCIONES} prefijoI18n="formularios.f051.recienNacido.atendio.opciones" prefijoId="f051-rn-atendio-neo" valores={estado.atendioNeonato} onChange={(v) => actualizar({ atendioNeonato: v })} />
      </div>
      <div className="flex flex-col gap-1">
        <Label>{t('formularios.f051.recienNacido.tamizajeAuditivo.titulo')}</Label>
        <ChecklistGrid
          claves={TAMIZAJE_AUDITIVO_OPCIONES}
          prefijoI18n="formularios.f051.recienNacido.tamizajeAuditivo.opciones"
          prefijoId="f051-rn-tamizaje-auditivo"
          valores={estado.tamizajeAuditivo}
          onChange={(v) => actualizar({ tamizajeAuditivo: v })}
        />
      </div>
    </div>
  );
}

function BloqueEgresoRn({ estado, onChange }: { readonly estado: EstadoEgresoRn051; readonly onChange: (v: EstadoEgresoRn051) => void }): JSX.Element {
  const { t } = useTranslation();
  function actualizar(cambios: Partial<EstadoEgresoRn051>): void {
    onChange({ ...estado, ...cambios });
  }
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f051.egresoRn.titulo')}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoSelect<CondicionEgresoRn>
          id="f051-erpn-condicion"
          label={t('formularios.f051.egresoRn.condicion')}
          value={estado.condicion}
          opciones={CONDICION_EGRESO_RN_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.egresoRn.condicionOpciones.${op}`)}
          onChange={(v) => actualizar({ condicion: v })}
        />
        <CampoTexto id="f051-erpn-hora" label={t('formularios.f051.egresoRn.hora')} value={estado.hora} onChange={(v) => actualizar({ hora: v })} />
        <CampoFecha id="f051-erpn-fecha" label={t('formularios.f051.egresoRn.fecha')} value={estado.fecha} onChange={(v) => actualizar({ fecha: v })} />
        <CampoSelect
          id="f051-erpn-fallece-durante"
          label={t('formularios.f051.egresoRn.falleceDuranteReferencia')}
          value={estado.falleceDuranteReferencia}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ falleceDuranteReferencia: v })}
        />
        <CampoSelect
          id="f051-erpn-fallece-lugar"
          label={t('formularios.f051.egresoRn.falleceEnLugarReferencia')}
          value={estado.falleceEnLugarReferencia}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ falleceEnLugarReferencia: v })}
        />
        <CampoTexto id="f051-erpn-establecimiento" label={t('formularios.f051.egresoRn.nombreEstablecimiento')} value={estado.nombreEstablecimiento} onChange={(v) => actualizar({ nombreEstablecimiento: v })} />
        <CampoSelect<AlimentoAlAlta>
          id="f051-erpn-alimento"
          label={t('formularios.f051.egresoRn.alimentoAlAlta')}
          value={estado.alimentoAlAlta}
          opciones={ALIMENTO_AL_ALTA_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.egresoRn.alimentoAlAltaOpciones.${op}`)}
          onChange={(v) => actualizar({ alimentoAlAlta: v })}
        />
        <CampoSelect
          id="f051-erpn-decubito"
          label={t('formularios.f051.egresoRn.decubitoSupino')}
          value={estado.decubitoSupino}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ decubitoSupino: v })}
        />
        {estado.edadMenor1Dia ? null : <CampoTexto id="f051-erpn-edad-dias" label={t('formularios.f051.egresoRn.edadDiasCompletos')} value={estado.edadDiasCompletos} onChange={(v) => actualizar({ edadDiasCompletos: v })} />}
        <CampoCheckbox id="f051-erpn-menor-1-dia" label={t('formularios.f051.egresoRn.edadMenor1Dia')} checked={estado.edadMenor1Dia} onChange={(v) => actualizar({ edadMenor1Dia: v })} />
        <CampoTexto id="f051-erpn-peso-egreso" label={t('formularios.f051.egresoRn.pesoAlEgreso')} value={estado.pesoAlEgreso} onChange={(v) => actualizar({ pesoAlEgreso: v })} />
        <CampoSelect
          id="f051-erpn-bcg"
          label={t('formularios.f051.egresoRn.bcg')}
          value={estado.bcg}
          opciones={NEAR_MISS_SI_NO_OPCIONES}
          etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
          onChange={(v) => actualizar({ bcg: v })}
        />
        <CampoTexto id="f051-erpn-cchcu" label={t('formularios.f051.egresoRn.certificadoCcHcu')} value={estado.certificadoCcHcu} onChange={(v) => actualizar({ certificadoCcHcu: v })} />
        <CampoTexto id="f051-erpn-nombre-rn" label={t('formularios.f051.egresoRn.nombreRecienNacido')} value={estado.nombreRecienNacido} onChange={(v) => actualizar({ nombreRecienNacido: v })} />
        <CampoTexto id="f051-erpn-responsable" label={t('formularios.f051.egresoRn.responsableEgresoRn')} value={estado.responsableEgresoRn} onChange={(v) => actualizar({ responsableEgresoRn: v })} />
      </div>
    </div>
  );
}

function BloqueEgresoMaterno({ estado, onChange }: { readonly estado: EstadoEgresoMaterno051; readonly onChange: (v: EstadoEgresoMaterno051) => void }): JSX.Element {
  const { t } = useTranslation();
  const m = 'formularios.f051.egresoMaterno';
  function actualizar(cambios: Partial<EstadoEgresoMaterno051>): void {
    onChange({ ...estado, ...cambios });
  }
  const siNoSelect = (id: string, clave: 'falleceDuranteReferencia' | 'falleceEnLugarReferencia' | 'anticoncepcionAsesoria'): JSX.Element => (
    <CampoSelect
      id={id}
      label={t(`${m}.${clave}`)}
      value={estado[clave]}
      opciones={NEAR_MISS_SI_NO_OPCIONES}
      etiquetaOpcion={(op) => t(`formularios.f051.nearMiss.opcionesSiNo.${op}`)}
      onChange={(v) => actualizar({ [clave]: v })}
    />
  );
  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-medium text-hce-ink">{t(`${m}.titulo`)}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
        <CampoSelect<CondicionEgresoMaterno>
          id="f051-erm-condicion"
          label={t(`${m}.condicion`)}
          value={estado.condicion}
          opciones={CONDICION_EGRESO_MATERNO_OPCIONES}
          etiquetaOpcion={(op) => t(`${m}.condicionOpciones.${op}`)}
          onChange={(v) => actualizar({ condicion: v })}
        />
        <CampoTexto id="f051-erm-hora" label={t(`${m}.hora`)} value={estado.hora} onChange={(v) => actualizar({ hora: v })} />
        <CampoFecha id="f051-erm-fecha" label={t(`${m}.fecha`)} value={estado.fecha} onChange={(v) => actualizar({ fecha: v })} />
        {siNoSelect('f051-erm-fallece-durante', 'falleceDuranteReferencia')}
        {siNoSelect('f051-erm-fallece-lugar', 'falleceEnLugarReferencia')}
        <CampoTexto id="f051-erm-dias" label={t(`${m}.diasCompletosDesdeParto`)} value={estado.diasCompletosDesdeParto} onChange={(v) => actualizar({ diasCompletosDesdeParto: v })} />
        <CampoTexto id="f051-erm-establecimiento" label={t(`${m}.nombreEstablecimientoResponsable`)} value={estado.nombreEstablecimientoResponsable} onChange={(v) => actualizar({ nombreEstablecimientoResponsable: v })} />
        <CampoTexto id="f051-erm-responsable" label={t(`${m}.responsableEgresoMaterno`)} value={estado.responsableEgresoMaterno} onChange={(v) => actualizar({ responsableEgresoMaterno: v })} />
      </div>

      <h4 className="text-sm font-medium text-hce-ink">{t(`${m}.anticoncepcionTitulo`)}</h4>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">{siNoSelect('f051-erm-asesoria', 'anticoncepcionAsesoria')}</div>
      {estado.anticoncepcionAsesoria === 'no' ? null : (
        <div className="flex flex-col gap-2">
          <Label>{t(`${m}.anticoncepcionPreferido`)}</Label>
          <ChecklistGrid claves={METODO_ANTICONCEPCION_OPCIONES} prefijoI18n={`${m}.metodoOpciones`} prefijoId="f051-erm-metodo-pref" valores={estado.anticoncepcionPreferido} onChange={(v) => actualizar({ anticoncepcionPreferido: v })} />
          <Label>{t(`${m}.anticoncepcionAccedido`)}</Label>
          <ChecklistGrid claves={METODO_ACCEDIDO_OPCIONES} prefijoI18n={`${m}.metodoOpciones`} prefijoId="f051-erm-metodo-acced" valores={estado.anticoncepcionAccedido} onChange={(v) => actualizar({ anticoncepcionAccedido: v })} />
        </div>
      )}
    </div>
  );
}

export interface EstadoPagina2Completa051 {
  readonly visual: EstadoPartogramaVisual051;
  readonly tablaHoraria: EstadoTablaHorariaPartograma051;
  readonly nearMiss: EstadoNearMiss051;
  readonly recienNacido: EstadoRecienNacido051;
  readonly egresoRn: EstadoEgresoRn051;
  readonly egresoMaterno: EstadoEgresoMaterno051;
}

export function estadoInicialPagina2Completa051(): EstadoPagina2Completa051 {
  return {
    visual: estadoInicialPartogramaVisual051(),
    tablaHoraria: estadoInicialTablaHorariaPartograma051(),
    nearMiss: estadoInicialNearMiss051(),
    recienNacido: estadoInicialRecienNacido051(),
    egresoRn: estadoInicialEgresoRn051(),
    egresoMaterno: estadoInicialEgresoMaterno051(),
  };
}

export interface Bloques051PartogramaProps {
  readonly estado: EstadoPagina2Completa051;
  readonly onChange: (estado: EstadoPagina2Completa051) => void;
}

export function Bloques051Partograma({ estado, onChange }: Bloques051PartogramaProps): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6">
      <h4 className="text-sm font-medium text-hce-ink">{t('formularios.f051.partograma.titulo')}</h4>
      <PartogramaVisual estado={estado.visual} onChange={(v) => onChange({ ...estado, visual: v })} />
      <TablaHoraria estado={estado.tablaHoraria} onChange={(v) => onChange({ ...estado, tablaHoraria: v })} />
      <BloqueNearMiss estado={estado.nearMiss} onChange={(v) => onChange({ ...estado, nearMiss: v })} />
      <BloqueRecienNacido estado={estado.recienNacido} onChange={(v) => onChange({ ...estado, recienNacido: v })} />
      <BloqueEgresoRn estado={estado.egresoRn} onChange={(v) => onChange({ ...estado, egresoRn: v })} />
      <BloqueEgresoMaterno estado={estado.egresoMaterno} onChange={(v) => onChange({ ...estado, egresoMaterno: v })} />
    </div>
  );
}
